import { useThreadRuntime } from "@assistant-ui/react";
import { ANALYTICS_EVENTS, trackEvent } from "@repo/ui/lib/analytics";
import { ChevronRight } from "lucide-react";
import { Item, ItemContent, ItemDescription, ItemTitle } from "@repo/ui/components/ui/item";
import { AskTigerLogo } from "./AskTigerLogo";
import { AskTigerSparkle } from "./AskTigerSparkle";
import { welcomeScreenStyles as s } from "./WelcomeScreen.styles";
import type { WelcomeScreenProps } from "./WelcomeScreen.types";
import type { CategoryId } from "./CategoryView.types";
import type { CapabilityCard } from "./capabilities";
import { MESSAGE_SUGGESTION_ID_KEY } from "./messageIdentity";

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

/** Card shape the welcome screen renders; capability cards normalize to this. */
interface WelcomeActionCard {
  id: string;
  label: string;
  description: string;
  category?: CategoryId;
  prompt: string;
  /** Sub-agent registry name the card belongs to, when the capability names one. */
  agent?: string;
}

/**
 * Sub-agents with a dedicated category view; their cards navigate there instead of
 * sending the prompt directly. Keyed by registry name (`CapabilityCard.agent`).
 */
const CATEGORY_BY_AGENT: Partial<Record<string, CategoryId>> = {
  scheduling: "oncall",
};

function toActionCard(card: CapabilityCard): WelcomeActionCard {
  return {
    id: card.id,
    label: card.label,
    description: card.description,
    category: CATEGORY_BY_AGENT[card.agent ?? card.id],
    prompt: card.prompt ?? card.examples?.[0] ?? card.label,
    ...(card.agent ? { agent: card.agent } : {}),
  };
}

export function WelcomeScreen({ userName, onSelectCategory, agentCards = [] }: WelcomeScreenProps) {
  const threadRuntime = useThreadRuntime();
  const cards = agentCards.map(toActionCard);

  const handleCardClick = (card: WelcomeActionCard, index: number) => {
    const opensCategory = Boolean(card.category && onSelectCategory);
    trackEvent(ANALYTICS_EVENTS.suggestionChipClicked, {
      source: "welcome_card",
      suggestionId: card.id,
      label: card.label,
      index,
      opensCategory,
      ...(card.category ? { category: card.category } : {}),
      ...(card.agent ? { agent: card.agent } : {}),
    });
    if (card.category && onSelectCategory) {
      onSelectCategory(card.category);
    } else {
      // Same visible text as typing it, tagged with the card id: the transport sends that as the
      // `suggestion_id` hint, which lets the agent skip its routing model call for this turn.
      threadRuntime.append({
        role: "user",
        content: [{ type: "text", text: card.prompt }],
        metadata: { custom: { [MESSAGE_SUGGESTION_ID_KEY]: card.id } },
      });
    }
  };

  return (
    <div className={s.root}>
      <AskTigerLogo className={s.avatar} />
      <h2 className={s.greeting}>
        {getGreeting()}
        {userName ? `, ${userName}` : ""}
      </h2>
      <p className={s.subtitle}>How can I help you today?</p>

      <div className={s.cardList}>
        {cards.map((card, index) => (
          <Item key={card.id} asChild variant="outline" size="sm" className={s.card}>
            <button onClick={() => handleCardClick(card, index)} className={s.cardBtn}>
              <span className={s.cardIcon}>
                <AskTigerSparkle size={20} />
              </span>
              <ItemContent className={s.cardContent}>
                <ItemTitle className={s.cardTitle}>{card.label}</ItemTitle>
                <ItemDescription className={s.cardDesc}>{card.description}</ItemDescription>
              </ItemContent>
              <ChevronRight className={s.cardChevron} />
            </button>
          </Item>
        ))}
      </div>
    </div>
  );
}
